<div class="flex flex-col px-8 pb-0 pt-3" data-test-id="desktop">
	<div class="section-inner flex w-full flex-col gap-4">
		<div class="flex flex-col-reverse">
			@if (title()) {
				@if (!hideMenu()) {
					<vp-link
						[link]="logoLink()"
						className="inline-flex"
						data-test-id="header-logo"
						type="link-unstyled"
					>
						<ng-container [ngTemplateOutlet]="logo" />
					</vp-link>
				} @else {
					<span class="mt-9.5 flex" data-test-id="header-logo">
						<ng-container [ngTemplateOutlet]="logo" />
					</span>
				}
			}
			@let metaMenuItems = metaMenu();
			@if (!hideMenu() && metaMenuItems) {
				<div
					[ngClass]="
						isNegativeHeader() ? 'text-light-primary' : 'text-black'
					"
					class="ml-auto flex gap-x-4"
					data-test-id="meta-menu"
				>
					@if (metaMenuItems.phoneNumber) {
						<a
							[href]="'tel:' + metaMenuItems.phoneNumber"
							class="my-1.5 flex h-fit items-center gap-x-2"
							data-test-id="header-phone-number"
						>
							<svg-icon
								class="size-4 shrink-0"
								src="assets/icons/icon-phone-line.svg"
							/>
							{{ metaMenuItems.phoneNumber }}
						</a>
					}
					@if (
						metaMenuItems.contactLink?.url &&
						!metaMenuItems.phoneNumber
					) {
						<vp-link
							[link]="metaMenuItems.contactLink"
							className="my-1.5 block h-fit"
							data-test-id="header-contact-link"
							type="link-unstyled"
						>
							{{ 'compositions.header.contact' | translate }}
						</vp-link>
					}
					@if (metaMenuItems.accountLink?.url) {
						<vp-link
							[link]="metaMenuItems.accountLink"
							className="my-1.5 flex h-fit items-center gap-x-2"
							type="link-unstyled"
						>
							<svg-icon
								class="size-4 shrink-0"
								src="assets/icons/icon-person.svg"
							/>
							{{ 'compositions.header.account' | translate }}
						</vp-link>
					}
					@if (metaMenuItems.showSearch) {
						<button class="my-1.5 h-fit">
							{{ 'compositions.header.search' | translate }}
						</button>
					}
					@if (metaMenuItems.languages; as languages) {
						<vp-dropdown
							[alignment]="'center'"
							[inputId]="'language'"
							[isWhite]="isNegativeHeader()"
							[label]="'global.language-using.label' | translate"
							[placeholder]="
								'global.dutch-abbreviation' | translate
							"
							[selectData]="languages.options"
							[useAsFormControl]="false"
							[value]="languages.selectedLanguageOption.path"
							(selectedValue)="languageSwitch.emit($event)"
							className="my-1.5 h-fit"
							data-test-id="language-dropdown"
							variant="detached"
						/>
					}
					@if (isCtaGroupResolved(); as cta) {
						<vp-dropdown-button
							[items]="cta.items"
							[title]="cta.title"
							data-test-id="cta-button"
						/>
					} @else if (isCtaItem(); as cta) {
						<vp-link
							[link]="cta"
							className="btn-sm h-fit"
							data-test-id="cta-button"
							type="button"
						>
							{{ cta.title }}
						</vp-link>
					}
				</div>
			}
		</div>
		@if (!hideMenu()) {
			<div
				#menuWrapper
				class="flex items-start"
				data-test-id="desktop-menu"
			>
				<ul
					class="flex h-fit flex-nowrap items-start gap-x-4 overflow-hidden"
					data-test-id="desktop-menu-items"
				>
					@for (menuItem of visibleMenuItems(); track menuItem) {
						<li
							[ngClass]="
								isNegativeHeader()
									? 'text-shadow text-white hover:border-white'
									: 'text-black hover:border-black'
							"
							[routerLinkActive]="'active'"
							[routerLinkActiveOptions]="{
								exact: menuItem.title.toLowerCase() === 'home',
							}"
							class="flex h-full shrink-0 items-center whitespace-nowrap border-b-2 border-transparent py-2 text-lg"
							data-test-class="desktop-menu-item"
						>
							@if (menuItem.url.startsWith('http')) {
								<a
									[attr.href]="menuItem.url"
									[target]="menuItem.target || '_self'"
									rel="noopener"
									type="link-unstyled"
								>
									{{ menuItem.title }}
								</a>
							} @else {
								<a
									[routerLink]="menuItem.url"
									type="link-unstyled"
								>
									{{ menuItem.title }}
								</a>
							}
						</li>
					}
				</ul>

				@if (hasOverflow()) {
					<div class="ml-4 shrink-0" data-test-id="more-menu-trigger">
						<vp-popover
							#moreMenuPopover="vpPopover"
							[closeable]="false"
							[isFullHeight]="true"
							(isOpen)="showingMoreMenu = $event"
							alignment="bottom-end"
							popoverId="popover"
							triggerId="more-menu-trigger-button"
						>
							<button
								#popoverTrigger
								[ngClass]="
									isNegativeHeader()
										? 'text-shadow text-white'
										: 'text-black'
								"
								class="flex h-full cursor-pointer items-center border-b-2 border-transparent py-2 text-lg"
								id="more-menu-trigger-button"
								vc-clicked
							>
								{{ 'compositions.header.more' | translate }}
								<svg-icon
									[ngClass]="
										showingMoreMenu ? 'rotate-180' : ''
									"
									class="ml-2 size-5 cursor-pointer"
									src="assets/icons/icon-chevron-down.svg"
								/>
							</button>
							<vp-popover-content>
								<vp-link-list
									[linkListItems]="moreMenuItems()"
									[showIcons]="false"
									(linkClicked)="moreMenuPopover.hide()"
								/>
							</vp-popover-content>
						</vp-popover>
					</div>
				}

				<!--
					Off-screen mirror of the full menu used purely to measure each
					item's natural width. Clipped to a zero-size box so it never
					affects layout, but offsetWidth still reports real widths.
				-->
				<div aria-hidden="true" class="relative size-0 overflow-hidden">
					<ul
						#measureList
						class="absolute flex flex-nowrap gap-x-4"
						data-test-id="desktop-menu-measure"
					>
						@for (menuItem of menuItems(); track menuItem) {
							<li
								class="shrink-0 whitespace-nowrap py-2 text-lg"
								data-measure-item
							>
								{{ menuItem.title }}
							</li>
						}
						<li
							#moreSample
							class="flex shrink-0 items-center whitespace-nowrap py-2 text-lg"
						>
							{{ 'compositions.header.more' | translate }}
							<span class="ml-2 inline-block size-5"></span>
						</li>
					</ul>
				</div>
			</div>
		}
	</div>

	<ng-template #logo>
		<svg-icon
			[ngClass]="isNegativeHeader() ? 'text-white' : 'text-dark-primary'"
			[src]="'assets/logos/' + title() + '.svg'"
			[svgStyle]="{ 'height.px': 44, width: 'auto' }"
		/>
	</ng-template>
</div>
